class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    this.setupChoEventListeners();
    this.initIdleState();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.showIdleState();
      return;
    }
    this.hideIdleState();
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    } else {
      this.saveRecent(this.getQuery());
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showIdleState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.showIdleState();
      return;
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=query,collection,article,page`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.hideIdleState();
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
  // ── CHO: Idle state ───────────────────────────────────────

  showIdleState() {
    this.renderRecent();
    const idleState = this.predictiveSearchResults.querySelector('#cho-idle-state');
    if (!idleState) return;
    idleState.style.display = 'block';
    idleState.removeAttribute('aria-hidden');
    // Open the dropdown using Dawn's own open() so maxHeight and aria are handled correctly
    this.setAttribute('results', true);
    this.open();
  }

  hideIdleState() {
    const idleState = this.predictiveSearchResults.querySelector('#cho-idle-state');
    if (!idleState) return;
    idleState.style.display = 'none';
    idleState.setAttribute('aria-hidden', 'true');
  }

  // ── CHO: Recent searches ──────────────────────────────────

  getRecent() {
    try { return JSON.parse(localStorage.getItem('cho_recent_searches') || '[]'); }
    catch { return []; }
  }

  saveRecent(query) {
    if (!query || query.trim().length < 2) return;
    const q = query.trim();
    let list = this.getRecent().filter(s => s.toLowerCase() !== q.toLowerCase());
    list.unshift(q);
    try { localStorage.setItem('cho_recent_searches', JSON.stringify(list.slice(0, 5))); }
    catch {}
  }

  renderRecent() {
    const section = this.predictiveSearchResults.querySelector('#cho-recent-section');
    const list    = this.predictiveSearchResults.querySelector('#cho-recent-list');
    if (!section || !list) return;
    const recent  = this.getRecent();
    if (!recent.length) { section.style.display = 'none'; return; }

    const clockIcon = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="1.5" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>`;

    section.style.display = 'block';
    list.innerHTML = recent.map(q => `
      <li role="option" aria-selected="false" class="predictive-search__list-item">
        <button type="button" class="cho-recent-item predictive-search__item link link--text" data-query="${q.replace(/"/g, '&quot;')}">
          <span class="cho-recent-icon">${clockIcon}</span>${q}
        </button>
      </li>`).join('');
  }

  // ── CHO: Delegated click handler ──────────────────────────

  setupChoEventListeners() {
    this.predictiveSearchResults.addEventListener('click', (e) => {

      // Brand chip clicked
      const chip = e.target.closest('.cho-chip');
      if (chip) {
        this.input.value = chip.dataset.query;
        this.saveRecent(chip.dataset.query);
        this.hideIdleState();
        this.input.form.submit();
        return;
      }

      // Recent search clicked
      const recent = e.target.closest('.cho-recent-item');
      if (recent) {
        this.input.value = recent.dataset.query;
        this.saveRecent(recent.dataset.query);
        this.hideIdleState();
        this.input.form.submit();
        return;
      }

      // Clear recent clicked
      if (e.target.closest('.cho-clear-recent')) {
        try { localStorage.removeItem('cho_recent_searches'); } catch {}
        this.renderRecent();
      }
    });
  }
  initIdleState() {
    const popularBrands = ['Barbour', 'Birkenstock', 'BOSS', 'GANT', 'Belstaff', 'UGG'];

    const idleHTML = `
      <div id="cho-idle-state" aria-hidden="true" style="display:none">
        <div id="cho-recent-section" class="cho-search-section" style="display:none">
          <div class="cho-search-section-header predictive-search__heading text-body caption-with-letter-spacing">
            <span>Recent Searches</span>
            <button type="button" class="cho-clear-recent" aria-label="Clear recent searches">Clear</button>
          </div>
          <ul id="cho-recent-list" class="predictive-search__results-list list-unstyled" role="group" aria-label="Recent searches"></ul>
        </div>
        <div id="cho-popular-section" class="cho-search-section">
          <div class="cho-search-section-header predictive-search__heading text-body caption-with-letter-spacing">
            <span>Popular Searches</span>
          </div>
          <ul class="cho-search-chips list-unstyled" role="group" aria-label="Popular searches">
            ${popularBrands.map(b => `
              <li role="option" aria-selected="false">
                <button type="button" class="cho-chip" data-query="${b}">${b}</button>
              </li>`).join('')}
          </ul>
        </div>
      </div>`;

    this.predictiveSearchResults.insertAdjacentHTML('afterbegin', idleHTML);
  }
}

customElements.define('predictive-search', PredictiveSearch);
